import { observable, action, makeObservable } from 'mobx';
import flatten from 'lodash/flatten';
import uniqBy from 'lodash/uniqBy';

import {
    CalculateCostParams,
    CalculateCostResponse,
    EhrPlanData,
    OptimalPlanQuery,
    OptimalPlanResponse,
    Optional,
    ServicePlanListItemData,
} from 'chaika-frontend-types/frontend-cms';
import { isDefined } from '@utils/isDefined';
import { toArrayFromIterable } from '@utils/toArrayFromIterable';
import { DateFormat, DateHelper } from '@utils/DateHelper';

export interface SingleServicePlansKeyParams extends CommonPlansKeyParams {
    serviceId: string;
}

export interface ManyServicesPlansKeyParams extends CommonPlansKeyParams {
    serviceIds: string[];
}

export interface CommonPlansKeyParams {
    ehrId: string;
    actualOn: string;
}

export class EhrsServicePlans {
    public static Name = 'ehrsServicePlans' as const;

    @observable public servicePlans = new Map<string, EhrPlanData[]>();
    @observable private optimalServicePlans = new Map<string, OptimalPlanResponse>();
    @observable private calculatedCosts = new Map<string, CalculateCostResponse[]>();

    public constructor() {
        makeObservable(this);
    }

    public getCalculatedCosts(ehrId: string, params: CalculateCostParams): Optional<CalculateCostResponse[]> {
        return this.calculatedCosts.get(this.toCalculateCostKey(ehrId, params));
    }

    public hasCalculatedCosts(ehrId: string, params: CalculateCostParams): boolean {
        return this.calculatedCosts.has(this.toCalculateCostKey(ehrId, params));
    }

    public getOptimalServicePlan(params: OptimalPlanQuery): Optional<OptimalPlanResponse> {
        return this.optimalServicePlans.get(this.toOptimalServicePlanKey(params));
    }

    public hasOptimalServicePlan(params: OptimalPlanQuery): boolean {
        return this.optimalServicePlans.has(this.toOptimalServicePlanKey(params));
    }

    @action
    public setOptimalServicePlan(params: OptimalPlanQuery, optimal: OptimalPlanResponse): void {
        this.optimalServicePlans.set(this.toOptimalServicePlanKey(params), optimal);
    }

    @action
    public setCalculatedCost(ehrId: string, params: CalculateCostParams, costs: CalculateCostResponse[]): void {
        this.calculatedCosts.set(this.toCalculateCostKey(ehrId, params), costs);
    }

    public getServicePlans(params: SingleServicePlansKeyParams | ManyServicesPlansKeyParams): Optional<EhrPlanData[]> {
        let servicePlans;

        if ('serviceId' in params) {
            servicePlans = this.getSingleServicePlan(params);
        } else {
            servicePlans = this.getManyServicePlans(params);
        }

        return servicePlans;
    }

    public getAllPlans(): EhrPlanData[] {
        return flatten(toArrayFromIterable(this.servicePlans));
    }

    public isLoaded(params: SingleServicePlansKeyParams): boolean {
        return this.servicePlans.has(this.makeServicePlanKey(params));
    }

    public setServicesPlans(ehrId: string, params: ServicePlanListItemData[], actualOn: string): void {
        params.forEach(({ serviceId, plans }) => {
            this.setServicePlans({ ehrId, serviceId, actualOn }, plans);
        });
    }

    public setServicePlans(params: SingleServicePlansKeyParams, plans: EhrPlanData[]) {
        this.servicePlans.set(this.makeServicePlanKey(params), plans);
    }

    public makeServicePlanKey({ ehrId, serviceId, actualOn }: SingleServicePlansKeyParams): string {
        return `${ehrId}_${serviceId}_${DateHelper.format(actualOn, DateFormat.DateWithDashSeparator)}`;
    }

    public reset(): void {
        this.servicePlans.clear();
        this.optimalServicePlans.clear();
        this.calculatedCosts.clear();
    }

    private getManyServicePlans(params: ManyServicesPlansKeyParams): Optional<EhrPlanData[]> {
        const { serviceIds, ehrId, actualOn } = params;
        const plans = flatten(
            serviceIds.map(serviceId => this.getSingleServicePlan({ ehrId, serviceId, actualOn })).filter(isDefined),
        );

        return plans.length > 0 ? uniqBy(plans, 'id') : undefined;
    }

    private getSingleServicePlan(params: SingleServicePlansKeyParams): Optional<EhrPlanData[]> {
        return this.servicePlans.get(this.makeServicePlanKey(params));
    }

    private toOptimalServicePlanKey({ ehrId, serviceId, date, authorId, organizationId }: OptimalPlanQuery): string {
        return `${ehrId}:${serviceId}:${date}:${authorId}:${organizationId}`;
    }

    private toCalculateCostKey(
        ehrId: string,
        { serviceId, actualOn, userId, organizationId }: CalculateCostParams,
    ): string {
        return `${ehrId}:${serviceId}:${actualOn}:${userId}:${organizationId}`;
    }
}

export const ehrsServicePlans = new EhrsServicePlans();
